import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation } from '@pv/common/hooks';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';

interface Organization {
  id: string;
  name: string;
  discardedAt: string;
}

export const DangerPanel = ({
  organization,
}: {
  organization: Organization;
}) => {
  const [archiveOrganizationModalOpen, setArchiveOrganizationModalOpen] =
    useState(false);
  const isArchived = !!organization.discardedAt;
  return (
    <Box padding={2}>
      <Typography variant="h4">Danger</Typography>
      <Button
        variant="contained"
        color="secondary"
        onClick={() => setArchiveOrganizationModalOpen(true)}
      >
        {isArchived ? 'Restore Organization' : 'Archive Organization'}
      </Button>
      <ArchiveOrganizationModal
        open={archiveOrganizationModalOpen}
        onClose={() => setArchiveOrganizationModalOpen(false)}
        organization={organization}
      />
    </Box>
  );
};

const archiveOrganizationMutation = gql`
  mutation ArchiveOrganization($input: AdminUpdateOrganizationInput!) {
    adminUpdateOrganization(input: $input) {
      organization {
        id
        name
        discardedAt
        venues {
          id
          name
          discardedAt
        }
      }
    }
  }
`;

const ArchiveOrganizationModal = ({
  open,
  onClose,
  organization,
}: {
  open: boolean;
  onClose: () => void;
  organization: Organization;
}) => {
  const navigate = useNavigate();
  const [archiveOrganization, { loading }] = useMutation(
    archiveOrganizationMutation,
    {
      onCompleted: () => {
        onClose();
        navigate(`/admin/organizations/${organization.id}/info`);
      },
    }
  );
  const isArchived = !!organization.discardedAt;
  const onConfirm = () => {
    archiveOrganization({
      variables: {
        input: { id: organization.id, archived: !isArchived },
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {isArchived ? 'Restore Organization' : 'Archive Organization'}
      </DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to {isArchived ? 'unarchive' : 'archive'} the{' '}
          {organization.name} organization and its venues?
        </Typography>
        {!isArchived && (
          <Typography>
            This remove all users from the organization and replace them with a
            placeholder user. If you want to undo it, you will have to re-invite
            the users.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" onClick={onConfirm}>
          {loading ? (
            <>
              Confirming <CircularProgress size={20} color="inherit" />
            </>
          ) : (
            'Confirm'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
